import { TFunction } from 'i18next';
import React from 'react';

export const colorText = (theme: string = 'light'): string => {
    if (theme === 'light') return '#000';
    return '#FFFFFF';
};

/**
 * 画像URLを変換する。
 * @param {String} relativeUrl
 * @returns
 */
export const convertToAbsoluteUrl = (relativeUrl: string | null): string => {
    if (relativeUrl === null) {
        return '';
    }
    try {
        const base = document.createElement('base');
        base.href = window.location.origin;
        document.head.appendChild(base);
        let cleanImageUrl = relativeUrl;
        if (cleanImageUrl.includes('url(')) {
            cleanImageUrl = relativeUrl.replace('url(', '').replace(')', '');
        }
        const absoluteUrl = new URL(cleanImageUrl, base.href);

        return absoluteUrl.href;
    } catch {
        return '';
    }
};

/**
 * 現在の言語に基づいて情報を取得します。
 * @param {object} response - サーバーからの応答データ。
 * @param {string} language - 現在の言語。
 * @returns {string}
 */
export const getValueByLanguage = (response: any, language?: string): string => {
    if (!response || !language) {
        return '';
    }

    // 中国語の言語コードの揺れを防止する
    if (language === 'zh' && !('zh' in response)) {
        return response['zh-CN'] ?? response['zh_cn'] ?? '';
    }
    if (language === 'zh-TW' && !('zh-TW' in response)) {
        return response['zh_tw'] ?? '';
    }

    return response[language] ?? '';
};

//注意事項にURLが含まれている場合、その文をリンクにする。
export const convertTextToLinks = (text: string): string[] | '' => {
    if (!text || typeof text !== 'string') {
        return '';
    }
    let txt = text;
    //URLの直後に全角文字がある場合はスペースを入れる
    // eslint-disable-next-line no-control-regex
    txt = txt.replace(/(https?:\/\/[ -~]+)([^\x01-\x7E])/g, '$1 $2');

    // 正規表現を使用してテキスト内のURLを検出
    const urlRegex = /https?:\/\/[^\s]+/g;

    // テキスト内のすべてのURLを見つける
    const urls = txt.match(urlRegex);

    // テキスト要素を格納するためのReact要素の配列を作成
    const elements = [];
    let lastIndex = 0;

    if (urls) {
        urls.forEach((url, index) => {
            // URLの前のテキストを追加
            const beforeText = txt.slice(lastIndex, txt.indexOf(url, lastIndex));
            elements.push(beforeText);

            // URLのリンクを追加
            elements.push(
                React.createElement('a', { key: index, href: url, target: '_blank', rel: 'noopener noreferrer' }, url),
            );

            lastIndex = txt.indexOf(url, lastIndex) + url.length;
        });
    }

    // 残りのテキストを追加
    const remainingText = txt.slice(lastIndex);
    if (remainingText) {
        elements.push(remainingText);
    }

    return elements;
};

/**
 * 全角文字を半角文字に変換する
 * @param {string} str
 * @returns {string} 半角文字
 */
export const kanaFullToHalf = (str: string): string => {
    const kanaMap: any = {
        ガ: 'ｶﾞ',
        ギ: 'ｷﾞ',
        グ: 'ｸﾞ',
        ゲ: 'ｹﾞ',
        ゴ: 'ｺﾞ',
        ザ: 'ｻﾞ',
        ジ: 'ｼﾞ',
        ズ: 'ｽﾞ',
        ゼ: 'ｾﾞ',
        ゾ: 'ｿﾞ',
        ダ: 'ﾀﾞ',
        ヂ: 'ﾁﾞ',
        ヅ: 'ﾂﾞ',
        デ: 'ﾃﾞ',
        ド: 'ﾄﾞ',
        バ: 'ﾊﾞ',
        ビ: 'ﾋﾞ',
        ブ: 'ﾌﾞ',
        ベ: 'ﾍﾞ',
        ボ: 'ﾎﾞ',
        パ: 'ﾊﾟ',
        ピ: 'ﾋﾟ',
        プ: 'ﾌﾟ',
        ペ: 'ﾍﾟ',
        ポ: 'ﾎﾟ',
        ヴ: 'ｳﾞ',
        ヷ: 'ﾜﾞ',
        ヺ: 'ｦﾞ',
        ア: 'ｱ',
        イ: 'ｲ',
        ウ: 'ｳ',
        エ: 'ｴ',
        オ: 'ｵ',
        カ: 'ｶ',
        キ: 'ｷ',
        ク: 'ｸ',
        ケ: 'ｹ',
        コ: 'ｺ',
        サ: 'ｻ',
        シ: 'ｼ',
        ス: 'ｽ',
        セ: 'ｾ',
        ソ: 'ｿ',
        タ: 'ﾀ',
        チ: 'ﾁ',
        ツ: 'ﾂ',
        テ: 'ﾃ',
        ト: 'ﾄ',
        ナ: 'ﾅ',
        ニ: 'ﾆ',
        ヌ: 'ﾇ',
        ネ: 'ﾈ',
        ノ: 'ﾉ',
        ハ: 'ﾊ',
        ヒ: 'ﾋ',
        フ: 'ﾌ',
        ヘ: 'ﾍ',
        ホ: 'ﾎ',
        マ: 'ﾏ',
        ミ: 'ﾐ',
        ム: 'ﾑ',
        メ: 'ﾒ',
        モ: 'ﾓ',
        ヤ: 'ﾔ',
        ユ: 'ﾕ',
        ヨ: 'ﾖ',
        ラ: 'ﾗ',
        リ: 'ﾘ',
        ル: 'ﾙ',
        レ: 'ﾚ',
        ロ: 'ﾛ',
        ワ: 'ﾜ',
        ヲ: 'ｦ',
        ン: 'ﾝ',
        ァ: 'ｧ',
        ィ: 'ｨ',
        ゥ: 'ｩ',
        ェ: 'ｪ',
        ォ: 'ｫ',
        ッ: 'ｯ',
        ャ: 'ｬ',
        ュ: 'ｭ',
        ョ: 'ｮ',
        '。': '｡',
        '、': '､',
        ー: 'ｰ',
        '「': '｢',
        '」': '｣',
        '・': '･',
        '　': ' ',
    };

    const reg = new RegExp('(' + Object.keys(kanaMap).join('|') + ')', 'g');

    return str
        .replace(reg, (s) => kanaMap[s])
        .replace(/゛/g, 'ﾞ')
        .replace(/゜/g, 'ﾟ');
};

/**
 * 使用開始可能曜日の表示
 * @param {Array<string>} enableActivateWeekdays -使用開始可能曜日の配列
 * @param {Function} t -i18n translator
 * @returns {string}
 */
export const displayAvailableDays = (enableActivateWeekdays: string[] | null, t: TFunction): string => {
    if (!enableActivateWeekdays || enableActivateWeekdays.length === 0) {
        return t('ticket-detail.available-only-specific'); // 値が存在しない場合: 特定の日にのみ使用できます。
    }

    const WEEKDAYS = {
        MONDAY: 'monday',
        TUESDAY: 'tuesday',
        WEDNESDAY: 'wednesday',
        THURSDAY: 'thursday',
        FRIDAY: 'friday',
        SATURDAY: 'saturday',
        SUNDAY: 'sunday',
        HOLIDAY: 'holiday',
    };

    const enableWeekdaysLength = enableActivateWeekdays.length;

    //すべての日に使用できない場合
    if (enableWeekdaysLength < 8) {
        const filteredWeekdays = enableActivateWeekdays.filter((item) => !item.includes('holiday'));
        const weekdaysLabel = filteredWeekdays
            .map((day) => {
                switch (day) {
                    case WEEKDAYS.MONDAY:
                        return t(`ticket-detail.weekdays.monday`);
                    case WEEKDAYS.TUESDAY:
                        return t(`ticket-detail.weekdays.tuesday`);
                    case WEEKDAYS.WEDNESDAY:
                        return t(`ticket-detail.weekdays.wednesday`);
                    case WEEKDAYS.THURSDAY:
                        return t(`ticket-detail.weekdays.thursday`);
                    case WEEKDAYS.FRIDAY:
                        return t(`ticket-detail.weekdays.friday`);
                    case WEEKDAYS.SATURDAY:
                        return t(`ticket-detail.weekdays.saturday`);
                    case WEEKDAYS.SUNDAY:
                        return t(`ticket-detail.weekdays.sunday`);
                    default:
                        return '';
                }
            })
            .join(t('ticket-detail.weekdays.delimiter') as string | undefined);

        // 休日がない場合、holidayLabelの前にドットを追加しない。
        const holidayLabel =
            filteredWeekdays.length === enableWeekdaysLength
                ? ` ${t('ticket-detail.weekdays.excludeHoliday')}` //カッコの前に半角スペースを入れます
                : `${
                      weekdaysLabel
                          ? t('ticket-detail.weekdays.delimiter') + t('ticket-detail.weekdays.holiday')
                          : t('ticket-detail.weekdays.holiday')
                  }`;

        return `${weekdaysLabel}${holidayLabel}`;
    } else {
        return t('ticket-detail.available-all-days'); // 8日以上ある場合、すべての日に使用できます。
    }
};

/**
 * テキストの幅を測定する
 * @param {string} text
 * @param {number} fontSize
 * @param {string} fontFamily
 */
const ctx = document.createElement('canvas').getContext('2d');
export const measureTextWidth = (text: string, fontSize: number, fontFamily: string = 'Noto Sans') => {
    if (ctx === null) {
        return 0;
    }
    ctx.font = `${fontSize}px "${fontFamily}"`;
    return ctx.measureText(text).width;
};

/**
 * フォントサイズを調整する
 * @param {string} text
 * @param {number} maxWidth
 * @param {number} initialFontSize
 * @param {string} fontFamily
 */
export const adjustFontSize = (
    text: string,
    maxWidth: number = window.innerWidth,
    initialFontSize: number = 18,
    fontFamily: string = 'Noto Sans',
) => {
    let fontSize = initialFontSize;
    // 最小フォントサイズを12pxに制限
    while (fontSize > 12) {
        const textWidth = measureTextWidth(text, fontSize, fontFamily);

        if (textWidth <= maxWidth) {
            return fontSize;
        }

        fontSize--; // テキストの幅が大きすぎる場合はフォントサイズを小さくする
    }

    return fontSize;
};
